import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import SocialGameCard from './SocialGameCard';
import LoadingState from '../common/LoadingState';
import EmptyState from '../common/EmptyState';

/**
 * Social Games List Component
 * Displays all logged games for a social session with edit/delete functionality
 */
const SocialGamesList = ({
  sessionId,
  playersById,
  currentUserId,
  refreshKey,
  onGameDeleted,
  scoringConfig,
}) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) return;
    fetchGames();
  }, [sessionId, refreshKey]);

  const fetchGames = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch games with player details
      const { data: gamesData, error: gamesError } = await supabase
        .from('session_games')
        .select(`
          id,
          team_1_score,
          team_2_score,
          winning_team,
          start_time,
          end_time,
          created_at,
          game_type_id,
          game_create_method,
          game_types (
            id,
            name,
            description
          ),
          players_session_games (
            player_id,
            team_number,
            result,
            pair_type,
            players (
              id,
              first_name,
              last_name,
              gender,
              is_guest,
              is_active
            )
          )
        `)
        .eq('session_id', sessionId)
        .eq('game_create_method', 'self_reported')
        .order('created_at', { ascending: false });

      if (gamesError) {
        throw gamesError;
      }

      setGames(gamesData || []);
    } catch (err) {
      console.error('Error fetching social games:', err);
      setError(err.message || 'Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const handleGameDeleted = (gameId) => {
    setGames(prev => prev.filter(g => g.id !== gameId));
    if (onGameDeleted) {
      onGameDeleted();
    }
  };

  if (loading) {
    return <LoadingState message="Loading games..." />;
  }

  if (error) {
    return (
      <div className="bg-danger-light border border-danger/30 rounded-lg p-4">
        <p className="text-danger-dark">Error: {error}</p>
        <button
          type="button"
          onClick={fetchGames}
          className="mt-2 px-4 py-2 bg-danger text-text-inverse rounded-md hover:opacity-90 transition-opacity"
        >
          Retry
        </button>
      </div>
    );
  }

  if (games.length === 0) {
    return (
      <EmptyState
        icon="🎮"
        title="No Games Yet"
        description="Games will appear here once players start logging matches."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-text-primary">
          Logged Games ({games.length})
        </h3>
        <button
          type="button"
          onClick={fetchGames}
          className="px-3 py-1 text-sm bg-primary/15 text-primary rounded-md hover:bg-primary/25 border border-border-medium transition-colors"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-4">
        {games.map((game) => (
          <SocialGameCard
            key={game.id}
            game={game}
            currentUserId={currentUserId}
            playersById={playersById}
            onGameUpdated={fetchGames}
            onGameDeleted={handleGameDeleted}
            scoringConfig={scoringConfig}
          />
        ))}
      </div>
    </div>
  );
};

export default SocialGamesList;

